/* eslint-disable react-hooks/exhaustive-deps */
import {
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type TableOptions,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import generateColumnsWithDefaultColumns from '@design-system/molecules/ticlick-table/default-columns';
import { useInitializeTableStates } from '@src/design-system/molecules/ticlick-table/hooks/use-initialize-table-states';
import { PersistentRowSelectionFeature } from '@src/design-system/molecules/ticlick-table/features/persistent-row-selection';

import { convertActiveFiltersToApiParams } from '@shared/features/views/helpers/active-filters-to-api-params';
import type {
  FetchFunction,
  UseTiclickTableConfigProps,
} from '@src/design-system/molecules/ticlick-table/types';
import { keepPreviousData } from '@tanstack/react-query';
import { getIsColumnOrderChanged } from '../utils/helpers/get-is-column-order-changed';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defaultTableConfig: TableOptions<any> = {
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getFacetedUniqueValues: getFacetedUniqueValues(),
  enableSortingRemoval: false,
  enableRowSelection: true,
  manualFiltering: true,
  manualPagination: true,
  enableColumnResizing: true,
  columnResizeMode: 'onChange',
  getRowId: (row) => row?.id,
  _features: [PersistentRowSelectionFeature],

  columns: [],
  data: [],
};

const useTiclickTableConfig = <T, F extends FetchFunction<T>>({
  options,
  rowActions,
  identifyingData,
  fetchingData,
}: UseTiclickTableConfigProps<T, F>) => {
  const { viewEntityType, viewEntityId } = identifyingData;
  const { query, options: queryOptions, fetchFn } = fetchingData || {};

  const {
    columnOrder,
    onColumnOrderChange,

    columnPinning,
    onColumnPinningChange,
    isColumnPinningChanged,

    columnVisibility,
    onColumnVisibilityChange,
    isColumnVisibilityChanged,

    columnNames,
    setColumnNames,
    isNamesChanged,

    columnSizing,
    onColumnSizingChange,

    sorting,
    onSortingChange,

    pagination,
    onPaginationChange,

    tableFilters,
    onGlobalFilterChange,

    currentView,
    setCurrentView,
    isViewFetched,
  } = useInitializeTableStates(identifyingData);

  const isAllowedToFetchTableData =
    (!viewEntityType && !viewEntityId) || isViewFetched;
  const queryOptions_ = useMemo(
    () => ({
      ...queryOptions,
      enabled: isAllowedToFetchTableData && (queryOptions?.enabled ?? true),
      placeholderData: keepPreviousData,
    }),
    [queryOptions, isAllowedToFetchTableData]
  );

  const filters = convertActiveFiltersToApiParams(tableFilters?.staticFilters);

  const queryData = fetchFn?.(
    {
      ...(typeof query === 'function'
        ? (query as (tableFilters: Parameters<F>[0]) => Parameters<F>[0])(
            tableFilters
          )
        : (query ?? ({} as Parameters<F>[0]))),
      ...filters,
      view: currentView?.id,
      page_size: pagination.pageSize,
      page_number: pagination.pageIndex + 1,
    },
    queryOptions_
  );
  const queryResults = queryData?.data?.results || [];

  const columns = useMemo(() => {
    return typeof options.columns === 'function'
      ? options.columns(queryResults)
      : options.columns || [];
  }, [options.columns, queryResults]);

  const data = useMemo(() => {
    return typeof options.data === 'function'
      ? options.data(queryResults)
      : queryResults;
  }, [JSON.stringify(options.data), JSON.stringify(queryResults)]);

  const finalColumns = useMemo(
    () => generateColumnsWithDefaultColumns(columns, rowActions),
    [columns, JSON.stringify(rowActions)]
  );

  const tableMetrics = useMemo(
    () => ({
      rowCount: queryData?.data?.count || 10,
      pageCount: queryData?.data?.total_pages || 1,
    }),
    [queryData?.data?.count, queryData?.data?.total_pages]
  );

  // === TABLE CONFIGURATION ===
  const table = useReactTable({
    ...defaultTableConfig,

    ...tableMetrics,
    ...options,

    data,
    columns: finalColumns,

    onColumnVisibilityChange,
    onColumnSizingChange,
    onColumnPinningChange,
    onColumnOrderChange,
    onPaginationChange,
    onSortingChange,
    onGlobalFilterChange,

    meta: {
      maxPinnedColumns: 3,

      getAllColumnPinnedCount(): number {
        const { left = [], right = [] } = columnPinning;
        return left.length + right.length;
      },

      getLeafColumnPinnedCount(): number {
        const { left = [], right = [] } = columnPinning;
        return [...left, ...right].filter(
          (p: string) => p !== 'select' && p !== 'actions'
        ).length;
      },

      isColumnPinningChanged,
      isColumnVisibilityChanged,
      isNamesChanged,
      isColumnOrderChanged: getIsColumnOrderChanged({
        currentColumnOrder: columnOrder,
        currentView,
        initialColumnOrder: [...columns.map((c) => c.id ?? ''), 'actions'],
      }),

      state: {
        columnNames,
        setColumnNames,

        currentView,
        setCurrentView,
      },

      identifyingData,
      queryData,

      ...options.meta,
    },

    state: useMemo(
      () => ({
        pagination,
        columnVisibility,
        columnPinning,
        columnSizing,
        sorting,
        columnOrder,
        globalFilter: tableFilters,
        ...options.state,
      }),
      [
        pagination,
        columnVisibility,
        columnPinning,
        columnSizing,
        columnOrder,
        sorting,
        tableFilters,
        options.state,
      ]
    ),
  });

  return table;
};

export default useTiclickTableConfig;
